import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContextNonNull } from "../../contexts/AppContext";
import UserTypesEnum from "../../models/UserTypesEnum";
import EndpointsEnum from "../../models/EndpointsEnum";
import IUpdateTokenSettingsDTO from "../../models/DTO/IUpdateTokenSettingsDTO";
import ILoadUserDTO from "../../models/DTO/ILoadUserDTO";
import UserService from "../../services/UserService";
import UIService from "../../services/UIService";
import { OwSidebar } from "../shared/OwSidebar/OwSidebar";
import ShopFloorStepView from "./ShopFloorStepView";
import useShopFloorData from "./useShopFloorData";
import "./ShopFloorLayout.scss";

const COMPACT_BREAKPOINT = 768;

const ShopFloorLayout: React.FC = () => {
    const { userModel, supplierModel, vendorModel, socket, uiContext } = useAppContextNonNull();
    const navigate = useNavigate();

    const userTypeId = userModel.supplierUserToken?.User_Type?.id;
    const isAdmin = userTypeId === UserTypesEnum.ADMIN || userTypeId === UserTypesEnum.MANAGEMENT;

    const {
        steps,
        activeStep,
        setActiveStep,
        serials,
        totalCount,
        employees,
        isLoading,
        isSerialsLoading,
        page,
        setPage,
        pageSize,
        setPageSize,
        search,
        setSearch,
        refreshData,
    } = useShopFloorData({ userModel, supplierModel, vendorModel, socket, isAdmin });

    const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(userModel.id ?? null);
    const [isCompact, setIsCompact] = useState(window.innerWidth < COMPACT_BREAKPOINT);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Theme state
    const getTheme = () => {
        const { themeType } = UIService.getThemeType(supplierModel, userModel);
        return themeType.toUpperCase() === "LIGHT";
    };
    const [isLightMode, setIsLightMode] = useState(getTheme());

    // Responsive breakpoint listener
    useEffect(() => {
        const handleResize = () => setIsCompact(window.innerWidth < COMPACT_BREAKPOINT);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const showHomeButton = isAdmin;

    const toggleTheme = () => {
        const newThemeMode = !isLightMode;
        setIsLightMode(newThemeMode);

        const currentToken = UserService.getCurrentToken(userModel);

        if (currentToken) {
            const payload: IUpdateTokenSettingsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                settings: {
                    theme: newThemeMode ? "LIGHT" : "DARK",
                },
                tokenGuid: currentToken.guid,
            };

            socket?.emit(EndpointsEnum.TokenUpdateSettings, payload);

            const loadPayload: ILoadUserDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
            };
            socket?.emit(EndpointsEnum.AppLoadUser, loadPayload);
        }
    };

    if (isLoading) {
        return (
            <div className="sf-layout">
                <div className="sf-layout__loading">
                    <div className="spinner-border" role="status" />
                    <span className="ms-3">Loading Shop Floor...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="sf-layout">
            <header className="sf-layout__header">
                <div className="sf-layout__header-left">
                    <h1 className="sf-layout__title">Shop Floor</h1>
                </div>

                <div className="sf-layout__header-center">
                    {isAdmin ? (
                        <>
                            <label className="sf-layout__label" htmlFor="sf-user-select">
                                User:
                            </label>
                            <select
                                id="sf-user-select"
                                className="form-select sf-layout__employee-select"
                                value={selectedEmployeeId ?? ""}
                                onChange={(e) => setSelectedEmployeeId(e.target.value ? Number(e.target.value) : null)}>
                                <option value="">Select user...</option>
                                {employees.map((emp) => (
                                    <option key={emp.id} value={emp.id}>
                                        {emp.name}
                                    </option>
                                ))}
                            </select>
                        </>
                    ) : (
                        <span className="sf-layout__label">{userModel.name}</span>
                    )}
                </div>

                <div className="sf-layout__header-right">
                    <button className="btn sf-layout__icon-btn" onClick={toggleTheme} title={isLightMode ? "Dark Mode" : "Light Mode"}>
                        <i className={isLightMode ? "fad fa-moon" : "fad fa-sun"} />
                    </button>
                    {showHomeButton && (
                        <button className="btn sf-layout__icon-btn" onClick={() => navigate("/dashboard")} title="Back to Dashboard">
                            <i className="fas fa-home" />
                        </button>
                    )}
                    <button className="btn sf-layout__icon-btn" onClick={() => setIsMenuOpen(true)} title="Menu">
                        <i className="fas fa-bars" />
                    </button>
                </div>
            </header>

            {isMenuOpen && (
                <OwSidebar position="right" width="280px" isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)}>
                    <div className="sf-layout__drawer-header">
                        <span className="sf-layout__drawer-title">Menu</span>
                        <button className="btn sf-layout__icon-btn" onClick={() => setIsMenuOpen(false)} title="Close">
                            <i className="fas fa-times" />
                        </button>
                    </div>
                    <button className="btn sf-layout__drawer-item" onClick={() => navigate("/logoff")}>
                        <i className="fad fa-sign-out" />
                        <span>Logout</span>
                    </button>
                </OwSidebar>
            )}

            <ShopFloorStepView
                steps={steps}
                activeStep={activeStep}
                onSelectStep={setActiveStep}
                serials={serials}
                totalCount={totalCount}
                page={page}
                pageSize={pageSize}
                isSerialsLoading={isSerialsLoading}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                searchValue={search}
                onSearchChange={setSearch}
                employees={employees}
                selectedEmployeeId={selectedEmployeeId}
                isAdmin={isAdmin}
                ctx={{ userModel, supplierModel, vendorModel }}
                refreshData={refreshData}
                notifyFn={uiContext.notifyFn}
                isCompact={isCompact}
            />
        </div>
    );
};

export default ShopFloorLayout;
